/**
 * IndicatorCard - Reusable Card Component for MT5-Style Grid
 *
 * Base component for all indicator cards in the scalable grid dashboard.
 * Supports collapsed (summary) and expanded (detailed) states.
 *
 * Design: Institutional-grade, dark theme, high contrast
 *
 * @version 1.0.0
 * @created 2025-12-21
 */

import React, { useState, memo } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

/**
 * IndicatorCard Component
 *
 * @param {string} title - Card title (e.g., "FIBONACCI")
 * @param {string} icon - Emoji or icon character (e.g., "📊")
 * @param {React.ReactNode} summary - Content shown when collapsed (1-2 key metrics)
 * @param {React.ReactNode} children - Detailed content shown when expanded
 * @param {string} badge - Optional badge text (e.g., "38.2%")
 * @param {string} badgeColor - Badge color (default: #27B56D)
 * @param {boolean} defaultExpanded - Start expanded (default: false)
 * @param {boolean} collapsible - Allow expand/collapse (default: true)
 * @param {string} status - Status indicator: 'bullish' | 'bearish' | 'neutral'
 */
const IndicatorCard = memo(({
    title,
    icon,
    summary,
    children,
    badge,
    badgeColor = '#27B56D',
    defaultExpanded = false,
    collapsible = true,
    status = 'neutral'
}) => {
    const [isExpanded, setIsExpanded] = useState(defaultExpanded);

    const statusColors = {
        bullish: '#22c55e',
        bearish: '#ef4444',
        neutral: '#fbbf24'
    };

    const statusColor = statusColors[status] || statusColors.neutral;

    return (
        <Box
            sx={{
                backgroundColor: '#0F0F12',
                border: '1px solid rgba(255, 255, 255, 0.06)',
                borderRadius: '8px',
                overflow: 'hidden',
                transition: 'all 0.2s ease',
                '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                }
            }}
        >
            {/* Card Header - Always Visible */}
            <Box
                onClick={collapsible ? () => setIsExpanded(!isExpanded) : undefined}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '8px 12px',
                    cursor: collapsible ? 'pointer' : 'default',
                    backgroundColor: 'rgba(255, 255, 255, 0.02)',
                    borderBottom: isExpanded ? '1px solid rgba(255, 255, 255, 0.06)' : 'none',
                    transition: 'background-color 0.15s ease',
                    '&:hover': collapsible ? {
                        backgroundColor: 'rgba(255, 255, 255, 0.04)'
                    } : {}
                }}
            >
                {/* Left: Icon + Title */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Status Indicator Dot */}
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            backgroundColor: statusColor,
                            boxShadow: `0 0 4px ${statusColor}`
                        }}
                    />

                    {/* Icon */}
                    {icon && (
                        <Typography sx={{ fontSize: '14px' }}>
                            {icon}
                        </Typography>
                    )}

                    {/* Title */}
                    <Typography
                        sx={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: '#E4E4E7',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            fontFamily: "'Inter', -apple-system, sans-serif"
                        }}
                    >
                        {title}
                    </Typography>

                    {/* Badge */}
                    {badge && (
                        <Box
                            sx={{
                                backgroundColor: `${badgeColor}20`,
                                color: badgeColor,
                                fontSize: '10px',
                                fontWeight: 700,
                                padding: '2px 6px',
                                borderRadius: '4px',
                                fontFamily: 'monospace'
                            }}
                        >
                            {badge}
                        </Box>
                    )}
                </Box>

                {/* Right: Expand/Collapse */}
                {collapsible && (
                    <IconButton
                        size="small"
                        sx={{
                            color: '#71717A',
                            padding: '2px',
                            '&:hover': { color: '#A1A1AA' }
                        }}
                    >
                        {isExpanded ? (
                            <ExpandLessIcon sx={{ fontSize: '16px' }} />
                        ) : (
                            <ExpandMoreIcon sx={{ fontSize: '16px' }} />
                        )}
                    </IconButton>
                )}
            </Box>

            {/* Summary Content - Always visible when collapsed */}
            {!isExpanded && summary && (
                <Box
                    sx={{
                        padding: '10px 12px',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center'
                    }}
                >
                    {summary}
                </Box>
            )}

            {/* Expanded Content */}
            <Collapse in={isExpanded}>
                <Box
                    sx={{
                        padding: '12px',
                        backgroundColor: 'rgba(0, 0, 0, 0.2)'
                    }}
                >
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
});

IndicatorCard.displayName = 'IndicatorCard';

export default IndicatorCard;
